import { useState, useEffect, createContext, useContext, useMemo } from 'react';
import { api } from '@/lib/api';
import { supabase } from '@/integrations/supabase/client';

// Define a custom User type matching our API response
interface User {
  id: string;
  email?: string;
  full_name?: string;
  role?: string;
  [key: string]: any;
}

interface Session {
  access_token: string;
  user: User;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: (credential: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  updatePassword: (password: string) => Promise<any>;
}

const registerDeviceSession = async (userId: string) => {
  try {
    let deviceSessionId = localStorage.getItem('device_session_id');
    if (!deviceSessionId) {
      deviceSessionId = crypto.randomUUID();
      localStorage.setItem('device_session_id', deviceSessionId);
    }
    const parser = navigator.userAgent;
    
    // Fetch Location and IP Data
    let ip = 'Unknown';
    let city = 'Unknown';
    let country = 'UN';
    try {
      const geoRes = await fetch('https://get.geojs.io/v1/ip/geo.json');
      const geoData = await geoRes.json();
      if (geoData.ip) {
        ip = geoData.ip;
        city = geoData.city;
        country = geoData.country_code; 
      }
    } catch(e) {
      // Fallback
      try {
        const fallbackRes = await fetch('https://api.ipify.org?format=json');
        const fallbackData = await fallbackRes.json();
        ip = fallbackData.ip;
      } catch (err) {}
    }

    const enhancedMetadata = JSON.stringify({ ua: parser, city, country, ip });

    await supabase.from('active_sessions' as any).upsert({
       session_id: deviceSessionId,
       user_id: userId,
       device_info: enhancedMetadata,
       last_active_ip: ip,
       created_at: new Date().toISOString()
    }, { onConflict: 'session_id' });
  } catch (err) {
    console.warn("Failed to register active device session", err);
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      const { data, error } = await api.auth.getUser();
      if (data?.user) {
        setUser(data.user);
        const token = localStorage.getItem('access_token');
        if (token) {
          setSession({ access_token: token, user: data.user });
        }
        await registerDeviceSession(data.user.id);
      }
      setLoading(false);
    };
    checkUser();
  }, []);

  const signUp = async (email: string, password: string, userData: any) => {
    const { data, error } = await api.auth.signUp({
      email,
      password,
      options: { data: userData }
    });
    if (data?.session) {
      setUser(data.session.user);
      setSession(data.session);
      localStorage.setItem('access_token', data.session.access_token);
      await registerDeviceSession(data.session.user.id);
    }
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await api.auth.signInWithPassword({
      email,
      password,
    });
    if (data?.session) {
      setUser(data.session.user);
      setSession(data.session);
      localStorage.setItem('access_token', data.session.access_token);
      await registerDeviceSession(data.session.user.id);
    }
    return { data, error };
  };

  const signInWithGoogle = async (credential: string) => {
    const { data, error } = await api.auth.signInWithIdToken({ provider: 'google', token: credential });
    if (data?.session) {
      setUser(data.session.user);
      setSession(data.session);
      localStorage.setItem('access_token', data.session.access_token);
      await registerDeviceSession(data.session.user.id);
    }
    return { data, error };
  };

  const signOut = async () => {
    await api.auth.signOut();
    setUser(null);
    setSession(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  useEffect(() => {
    if (!user) return;
    const deviceSessionId = localStorage.getItem('device_session_id');
    if (!deviceSessionId) return;

    let active = true;
    let schemaFailed = false;

    // Fallback Background Hearthbeat to verify Session Validity
    // Checks every 15 seconds to see if an Admin or another device revoked this session
    const interval = setInterval(async () => {
      if (!active || schemaFailed || document.hidden) return;
      try {
        const { data, error } = await supabase
          .from('active_sessions' as any)
          .select('id')
          .eq('session_id', deviceSessionId)
          .maybeSingle();

        // PostgREST often returns generic 400s for missing tables, instead of just 42P01.
        // Halt polling on ANY error to prevent console spam until the DB is fully synced
        if (error) {
           schemaFailed = true;
           return;
        }

        if (!data && active) {
           console.warn("Session Revoked Remotely. Terminating Local Context.");
           await signOut();
        }
      } catch (err) {}
    }, 60000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [user]);

  const resetPassword = async (email: string) => {
    // URL to redirect the user to after they click the magic link
    const redirectUrl = `${window.location.origin}/update-password`;
    return await api.auth.resetPasswordForEmail(email, redirectUrl);
  };

  const updatePassword = async (password: string) => {
    return await api.auth.updateUser({ password });
  };

  const contextValue = useMemo(() => ({
    user: user?.id ? user : null,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
  }), [user?.id, session, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
